import React, { useState, useEffect, useMemo } from 'react';
import { Users, UserCheck, Shield, Tag, ChevronDown, ChevronUp, X, Loader2, Info, Zap, UserPlus } from 'lucide-react';
import api from '../../utils/api';

// Cache the available placeholders so we don't re-fetch on every expand
let placeholderCache = null;
let cacheTimestamp = 0;
const CACHE_TTL = 60000; // 1 minute

const CATEGORY_META = {
    relationship: { label: 'Relationships', icon: UserCheck, color: 'bg-blue-50 text-blue-700 border-blue-200', chipColor: 'bg-blue-100 text-blue-700', activeRing: 'ring-blue-300' },
    team: { label: 'Team', icon: Users, color: 'bg-green-50 text-green-700 border-green-200', chipColor: 'bg-green-100 text-green-700', activeRing: 'ring-green-300' },
    company: { label: 'Company-Wide', icon: Users, color: 'bg-orange-50 text-orange-700 border-orange-200', chipColor: 'bg-orange-100 text-orange-700', activeRing: 'ring-orange-300' },
    role: { label: 'Roles', icon: Shield, color: 'bg-purple-50 text-purple-700 border-purple-200', chipColor: 'bg-purple-100 text-purple-700', activeRing: 'ring-purple-300' },
    custom: { label: 'Custom', icon: Tag, color: 'bg-teal-50 text-teal-700 border-teal-200', chipColor: 'bg-teal-100 text-teal-700', activeRing: 'ring-teal-300' },
};

const AssignmentPlaceholderPicker = ({ value = [], onChange }) => {
    const [allPlaceholders, setAllPlaceholders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        const fetchPlaceholders = async () => {
            // Use cache if fresh
            if (placeholderCache && Date.now() - cacheTimestamp < CACHE_TTL) {
                setAllPlaceholders(placeholderCache);
                return;
            }

            setLoading(true);
            try {
                const res = await api.get('/placeholders/available');
                if (res.data?.success) {
                    placeholderCache = res.data.assignment_placeholders || [];
                    cacheTimestamp = Date.now();
                    setAllPlaceholders(placeholderCache);
                }
            } catch (err) {
                console.error('Failed to fetch placeholders:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchPlaceholders();
    }, []);

    // Group placeholders by category
    const grouped = useMemo(() => {
        const groups = {};
        for (const p of allPlaceholders) {
            const cat = p.category || 'custom';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(p);
        }
        return groups;
    }, [allPlaceholders]);

    const selectedSet = useMemo(() => new Set(value), [value]);
    const hasPlaceholders = value.length > 0;

    const toggle = (slug) => {
        const next = selectedSet.has(slug)
            ? value.filter(v => v !== slug)
            : [...value, slug];
        onChange(next);
    };

    const remove = (slug) => {
        onChange(value.filter(v => v !== slug));
    };

    // Resolve display info for a slug
    const getDisplayInfo = (slug) => {
        const found = allPlaceholders.find(p => p.slug === slug);
        if (found) {
            const meta = CATEGORY_META[found.category] || CATEGORY_META.custom;
            return { name: found.name, chipColor: meta.chipColor, description: found.description };
        }
        return { name: slug, chipColor: 'bg-gray-100 text-gray-700', description: '' };
    };

    return (
        <div className="space-y-2">
            {/* ── Assignment Status Banner ─────────────────────────────── */}
            {!hasPlaceholders ? (
                /* Default: Auto-assign to course employee – make it VERY obvious */
                <div className="relative overflow-hidden rounded-xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 via-green-50 to-emerald-50">
                    <div className="absolute inset-y-0 left-0 w-1 bg-emerald-500" />
                    <div className="flex items-start gap-3 px-4 py-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                                <Zap className="w-4 h-4 text-emerald-600" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-emerald-800">Auto-Assigned to Course Employee</span>
                                <span className="px-1.5 py-0.5 bg-emerald-200 text-emerald-700 text-[10px] font-bold uppercase rounded-full tracking-wide">Default</span>
                            </div>
                            <p className="text-xs text-emerald-600 mt-0.5 leading-relaxed">
                                This activity will automatically be assigned to whoever is enrolled in the course. No manual assignment needed.
                            </p>
                        </div>
                    </div>
                </div>
            ) : (
                /* Custom placeholders active – show a distinct warning-style banner */
                <div className="relative overflow-hidden rounded-xl border-2 border-violet-200 bg-gradient-to-r from-violet-50 via-purple-50 to-violet-50">
                    <div className="absolute inset-y-0 left-0 w-1 bg-violet-500" />
                    <div className="flex items-start gap-3 px-4 py-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                                <UserPlus className="w-4 h-4 text-violet-600" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-violet-800">Custom Assignment Placeholders</span>
                                <span className="px-1.5 py-0.5 bg-violet-200 text-violet-700 text-[10px] font-bold uppercase rounded-full tracking-wide">
                                    {value.length} active
                                </span>
                            </div>
                            <p className="text-xs text-violet-600 mt-0.5 leading-relaxed">
                                This activity is assigned to specific roles/groups instead of just the course employee.
                            </p>

                            {/* Selected placeholder chips */}
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {value.map(slug => {
                                    const { name, chipColor } = getDisplayInfo(slug);
                                    return (
                                        <span
                                            key={slug}
                                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm border border-white/50 ${chipColor}`}
                                        >
                                            {name}
                                            <button
                                                type="button"
                                                onClick={() => remove(slug)}
                                                className="hover:bg-black/10 rounded-full p-0.5 -mr-0.5 transition-colors"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Configure button ─────────────────────────────────────── */}
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className={`w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-lg border transition-all ${expanded
                    ? 'text-violet-700 bg-violet-50 border-violet-200'
                    : 'text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-100 hover:text-gray-700'
                    }`}
            >
                <Users className="w-3.5 h-3.5" />
                {expanded ? 'Hide Assignment Options' : 'Change Assignment'}
                {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>

            {/* ── Placeholder picker panel ──────────────────────────────── */}
            {expanded && (
                <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                    {/* Info tip */}
                    <div className="px-3 py-2 bg-blue-50 border-b border-blue-100 flex items-start gap-2">
                        <Info className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <p className="text-[11px] text-blue-700 leading-relaxed">
                            <strong>Placeholders</strong> let you assign this activity to specific roles or groups relative to the employee. If none are selected, the activity auto-assigns to the course employee.
                        </p>
                    </div>

                    {loading ? (
                        <div className="p-4 flex items-center justify-center gap-2 text-gray-400">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-xs">Loading placeholders...</span>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {Object.entries(grouped).map(([category, items]) => {
                                const meta = CATEGORY_META[category] || CATEGORY_META.custom;
                                const Icon = meta.icon;
                                return (
                                    <div key={category} className="p-3">
                                        <div className="flex items-center gap-1.5 mb-2">
                                            <Icon className="w-3.5 h-3.5 text-gray-500" />
                                            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                                                {meta.label}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {items.map(p => {
                                                const isSelected = selectedSet.has(p.slug);
                                                return (
                                                    <button
                                                        key={p.slug}
                                                        type="button"
                                                        onClick={() => toggle(p.slug)}
                                                        title={p.description}
                                                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${isSelected
                                                            ? `${meta.color} ring-2 ring-offset-1 ${meta.activeRing} shadow-sm`
                                                            : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-100'
                                                            }`}
                                                    >
                                                        {isSelected && <span className="mr-1">✓</span>}
                                                        {p.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}

                            {Object.keys(grouped).length === 0 && (
                                <div className="p-4 text-center text-xs text-gray-400">
                                    No placeholders available. Create custom placeholders in Settings.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Quick reset to default */}
                    {hasPlaceholders && (
                        <div className="px-3 py-2 bg-gray-50 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={() => onChange([])}
                                className="w-full text-xs text-center text-emerald-600 hover:text-emerald-700 font-medium py-1 hover:bg-emerald-50 rounded-lg transition-colors"
                            >
                                ↩ Reset to Auto-Assign (Course Employee)
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AssignmentPlaceholderPicker;
